import { useState } from 'react'
import { Icon } from '@iconify/react'
import type { MascotGameState, Need, Room } from './mascot-game-types'
import { NEEDS, projectNeeds } from './mascot-game-view'
import styles from './MascotVitals.module.css'

type Indicator = Need | 'xp'

export function MascotVitals({
  state,
  now,
  onRoom,
  carnivore = false,
  herbivore = false,
}: {
  state: MascotGameState
  now: number
  onRoom: (room: Room) => void
  carnivore?: boolean
  herbivore?: boolean
}) {
  const [selected, setSelected] = useState<Indicator | null>(null)
  const needs = projectNeeds(state, now)
  const progress = state.progress ?? {
    current: state.xp % 100,
    required: 100,
    percent: state.level === 50 ? 100 : state.xp % 100,
  }
  const indicators = [
    {
      id: 'xp' as const,
      name: 'Experiência',
      icon: 'mdi:star-four-points',
      room: 'journal' as Room,
      value: progress.percent,
      summary: `${Math.round(progress.percent)}% · Nv ${state.level}`,
      detail:
        state.level === 50
          ? `Nível ${state.level} · máximo`
          : `${progress.current}/${progress.required} XP · nível ${state.level}`,
    },
    ...NEEDS.map((need) => ({
      id: need.id,
      name: need.name,
      icon:
        need.id === 'satiety' && herbivore
          ? 'mdi:grass'
          : carnivore && need.id === 'satiety'
            ? 'mdi:food-steak'
            : need.icon,
      room: need.room,
      value: needs[need.id],
      summary: `${Math.round(needs[need.id])}%`,
      detail: `${Math.round(needs[need.id])}%`,
    })),
  ]

  return (
    <aside className={styles.rail} aria-label="Indicadores do mascote">
      {indicators.map((indicator) => {
        const percent = Math.max(0, Math.min(100, Math.round(indicator.value)))
        const expanded = selected === indicator.id
        const dangerous = indicator.id !== 'xp' && percent < 30
        return (
          <button
            key={indicator.id}
            type="button"
            className={styles.indicator}
            data-need={indicator.id}
            data-low={dangerous}
            aria-expanded={expanded}
            aria-label={`${indicator.name}: ${percent}%. ${indicator.detail}`}
            title={
              dangerous
                ? `Em perigo. Toque para cuidar do mascote. ${indicator.name} · ${indicator.detail}`
                : `${indicator.name} · ${indicator.detail}`
            }
            onClick={() => {
              setSelected(expanded ? null : indicator.id)
              if (!expanded) onRoom(indicator.room)
            }}
          >
            <span
              className={styles.ring}
              role="progressbar"
              aria-label={
                indicator.id === 'xp'
                  ? 'Experiência para o próximo nível'
                  : indicator.name
              }
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <svg viewBox="0 0 48 48" aria-hidden="true">
                <circle className={styles.track} cx="24" cy="24" r="20" />
                <circle
                  className={styles.fill}
                  cx="24"
                  cy="24"
                  r="20"
                  pathLength="100"
                  strokeDasharray={`${percent} 100`}
                />
              </svg>
              <Icon icon={indicator.icon} aria-hidden="true" />
            </span>
            {expanded && (
              <span className={styles.detail} aria-hidden="true">
                <strong>{indicator.id === 'xp' ? 'XP' : indicator.name}</strong>
                <small>{indicator.summary}</small>
              </span>
            )}
            {dangerous && !expanded && (
              <span
                className={styles.dangerPointer}
                data-danger-pointer
                aria-hidden="true"
              >
                <Icon icon="mdi:arrow-left-bold" />
              </span>
            )}
            {dangerous && (
              <span className={styles.attention} aria-hidden="true">
                !
              </span>
            )}
          </button>
        )
      })}
    </aside>
  )
}
